import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  List,
  Stack,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

import ConfirmDialog from "components/ConfirmDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController, setMessage } from "context";
import { normalizeString } from "helpers/normalizeString";
import type { ICategoriaProduto } from "interfaces/loja";
import {
  createCategoria,
  deleteCategoria,
  getCategoriasArvore,
  updateCategoria,
} from "services/lojaCategorias";
import { LojaPageContainer } from "../common";
import {
  CATEGORIAS_ACCENT,
  categoriasToolbarSx,
  emptyStateSx,
  fieldSx,
  saveButtonSx,
  treePanelSx,
} from "./categorias.theme";
import CategoryTreeItem from "./components/CategoryTreeItem";
import CategoriaDetailPanel from "./components/CategoriaDetailPanel";
import CategoriaFormDialog from "./components/CategoriaFormDialog";
import {
  collectAllIds,
  computeStats,
  filterTree,
  flattenTree,
  getParentCategory,
} from "./components/categorias.helpers";

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? CATEGORIAS_ACCENT : "text.primary"}
        sx={{ fontSize: { xs: "1.25rem", sm: "2rem" } }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function LojaCategorias() {
  const [, dispatch] = useMaterialUIController();
  const [tree, setTree] = useState<ICategoriaProduto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<ICategoriaProduto | null>(null);

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<ICategoriaProduto | null>(null);
  const [initialParentId, setInitialParentId] = useState<number | undefined>();
  const [saving, setSaving] = useState(false);

  const [openDelete, setOpenDelete] = useState<{
    open: boolean;
    id?: number;
    nome?: string;
  }>({ open: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getCategoriasArvore();
      if (resp.status === 200) setTree(resp.data || []);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Falha ao carregar categorias.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selectedCategory) return;
    const atualizado = flattenTree(tree).find((c) => c.id === selectedCategory.id);
    if (atualizado) setSelectedCategory(atualizado);
  }, [tree, selectedCategory?.id]);

  const flatCategories = useMemo(() => flattenTree(tree), [tree]);
  const stats = useMemo(() => computeStats(tree), [tree]);
  const isFiltered = Boolean(search.trim());

  const displayTree = useMemo(
    () => filterTree(tree, search),
    [tree, search],
  );

  useEffect(() => {
    if (!search.trim()) return;
    setExpanded(new Set(collectAllIds(displayTree)));
  }, [search, displayTree]);

  const toggleExpanded = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpanded(new Set(collectAllIds(tree)));
  }, [tree]);

  const collapseAll = useCallback(() => {
    setExpanded(new Set());
  }, []);

  const openCreate = useCallback((parent?: ICategoriaProduto) => {
    setEditing(null);
    setInitialParentId(parent?.id);
    setOpenForm(true);
  }, []);

  const openEdit = useCallback((cat: ICategoriaProduto) => {
    setEditing(cat);
    setInitialParentId(undefined);
    setSelectedCategory(cat);
    setOpenForm(true);
  }, []);

  const requestDelete = useCallback((cat: ICategoriaProduto) => {
    setOpenDelete({ open: true, id: cat.id, nome: cat.nome });
  }, []);

  const onSave = useCallback(
    async (form: { nome: string; categoriaPaiId?: number }) => {
      setSaving(true);
      try {
        const payload = {
          nome: form.nome,
          slug: normalizeString(form.nome),
          categoriaPaiId: form.categoriaPaiId,
        };
        if (editing) {
          await updateCategoria(String(editing.id), payload);
          setMessage(dispatch, {
            open: true,
            severity: "success",
            text: "Categoria atualizada.",
          });
        } else {
          await createCategoria(payload);
          setMessage(dispatch, {
            open: true,
            severity: "success",
            text: "Categoria criada.",
          });
        }
        setOpenForm(false);
        await load();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Falha ao salvar categoria.";
        setMessage(dispatch, { open: true, severity: "error", text: msg });
      } finally {
        setSaving(false);
      }
    },
    [editing, dispatch, load],
  );

  const onConfirmDelete = useCallback(async () => {
    if (!openDelete.id) return;
    try {
      await deleteCategoria(String(openDelete.id));
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Categoria excluída.",
      });
      setOpenDelete({ open: false });
      if (selectedCategory?.id === openDelete.id) setSelectedCategory(null);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { status?: number }; message?: string };
      const msg =
        err?.response?.status === 409
          ? "Não é possível excluir: há subcategorias ou produtos vinculados."
          : err?.message || "Falha ao excluir categoria.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
    }
  }, [openDelete, selectedCategory?.id, dispatch, load]);

  const parentNome = selectedCategory
    ? getParentCategory(flatCategories, selectedCategory.categoriaPaiId)?.nome ?? null
    : null;

  const filteredCount = flattenTree(displayTree).length;

  return (
    <LojaPageContainer
      title="Categorias"
      subtitle="Organize o catálogo em categorias e subcategorias para facilitar a navegação na loja."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={() => openCreate()}
          sx={saveButtonSx}
        >
          Nova categoria
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini
            label={isFiltered ? "No filtro" : "Total cadastradas"}
            value={isFiltered ? filteredCount : stats.total}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Categorias raiz" value={stats.raiz} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Com subcategorias" value={stats.comFilhos} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Níveis na árvore" value={stats.profundidade || "—"} />
        </Grid>
      </Grid>

      <MDBox sx={categoriasToolbarSx}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountTreeOutlinedIcon sx={{ color: CATEGORIAS_ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {isFiltered
                ? `${filteredCount} categoria${filteredCount === 1 ? "" : "s"} no filtro`
                : `${stats.total} categoria${stats.total === 1 ? "" : "s"} cadastrada${stats.total === 1 ? "" : "s"}`}
            </MDTypography>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-end" }}>
            <FormControl variant="outlined" size="small" fullWidth sx={fieldSx}>
              <InputLabel shrink htmlFor="categorias-search">
                Buscar
              </InputLabel>
              <OutlinedInput
                id="categorias-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                label="Buscar"
                notched
                placeholder="Nome, slug ou descrição…"
                endAdornment={
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                }
              />
            </FormControl>
            <Stack direction="row" spacing={1} flexShrink={0}>
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                startIcon={<UnfoldMoreIcon />}
                onClick={expandAll}
                disabled={!tree.length}
              >
                Expandir
              </MDButton>
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                startIcon={<UnfoldLessIcon />}
                onClick={collapseAll}
                disabled={!tree.length}
              >
                Recolher
              </MDButton>
            </Stack>
          </Stack>
        </Stack>
      </MDBox>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <MDTypography variant="button" fontWeight="medium" mb={1} display="block">
            Árvore de categorias
          </MDTypography>

          {loading ? (
            <MDBox sx={{ ...treePanelSx, p: 4, textAlign: "center" }}>
              <CircularProgress size={32} sx={{ color: CATEGORIAS_ACCENT }} />
            </MDBox>
          ) : tree.length === 0 ? (
            <MDBox sx={emptyStateSx}>
              <AccountTreeOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
              <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                Nenhuma categoria cadastrada
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" mb={2}>
                Crie categorias raiz e subcategorias para organizar seus produtos.
              </MDTypography>
              <MDButton
                variant="gradient"
                color="dark"
                startIcon={<AddOutlinedIcon />}
                onClick={() => openCreate()}
                sx={saveButtonSx}
              >
                Criar primeira categoria
              </MDButton>
            </MDBox>
          ) : displayTree.length === 0 ? (
            <MDBox sx={emptyStateSx}>
              <MDTypography variant="body2" color="text.secondary">
                Nenhuma categoria corresponde à busca.
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox sx={treePanelSx}>
              <List component="nav" disablePadding sx={{ p: 1 }}>
                {displayTree.map((categoria) => (
                  <CategoryTreeItem
                    key={categoria.id}
                    categoria={categoria}
                    expanded={expanded}
                    searchTerm={search}
                    onToggle={toggleExpanded}
                    onSelect={setSelectedCategory}
                    selectedId={selectedCategory?.id}
                    onEdit={openEdit}
                    onDelete={requestDelete}
                    onAddSub={openCreate}
                  />
                ))}
              </List>
            </MDBox>
          )}
        </Grid>

        <Grid item xs={12} md={7}>
          <MDTypography variant="button" fontWeight="medium" mb={1} display="block">
            Painel de detalhes
          </MDTypography>
          <CategoriaDetailPanel
            categoria={selectedCategory}
            parentNome={parentNome}
            onAddSub={openCreate}
            onEdit={openEdit}
            onDelete={requestDelete}
          />
        </Grid>
      </Grid>

      <CategoriaFormDialog
        open={openForm}
        editing={editing}
        flatCategories={flatCategories}
        initialParentId={initialParentId}
        saving={saving}
        onClose={() => setOpenForm(false)}
        onSave={onSave}
      />

      <ConfirmDialog
        open={openDelete.open}
        title="Confirmar exclusão"
        message={`Excluir a categoria "${openDelete.nome ?? ""}" permanentemente? Subcategorias ou produtos vinculados impedirão a exclusão.`}
        onClose={() => setOpenDelete({ open: false })}
        onConfirm={onConfirmDelete}
      />
    </LojaPageContainer>
  );
}
